<template>
  <div
    class="missed-conversation flex items-center"
    :class="[modelValue.delaySeverity && !modelValue.isActioned ? 'delayed-' + modelValue.delaySeverity : '']"
  >
    <div class="w-full flex items-center gap-2">
      <!-- Conversation Details -->
      <div
        class="min-h-[80px] pl-3 w-full py-3 px-6 border border-secondary rounded-xl flex gap-6 items-center"
        :class="{ 'bg-secondary': modelValue.isActioned }"
      >
        <!-- Type and Timestamp -->
        <div
          class="w-[70px] flex-shrink-0 min-h-[60px] border border-secondary px-2 py-1 rounded flex flex-col items-center justify-center gap-1"
          :class="{
            'apply-severity': !modelValue.isActioned,
            'bg-secondary': !modelValue.isActioned,
            'bg-tertiary': modelValue.isActioned
          }"
        >
          <Icon
            :type="getTypeIcon(modelValue.type)"
            fa-style="fad"
            class="text-lg"
          />
          <div class="text-sm">{{ formatTimestampToParts(modelValue.createdAt, 1, true) }}</div>
        </div>

        <!-- Details -->
        <div class="w-[240px] flex-shrink-0">
          <div class="flex flex-col items-left justify-center h-10 leading-5">
            <div v-if="modelValue.agreementId">
              <RouterLink
                :to="agreementLink"
                class="link !font-bold"
              >
                {{ modelValue.customerName }}
              </RouterLink>
            </div>
            <div
              v-else
              class="link"
            >
              <Dropdown>
                <template #triggerContent>
                  <Button
                    size="xs"
                    icon="link"
                    label="Link to customer"
                    text
                    :style="{ color: 'var(--text-brand-solid)' }"
                  />
                </template>

                <Select
                  v-model="selectedAgreementId"
                  v-model:search-term="searchTermModel"
                  placeholder="Search for customer"
                  :options="agreementsList || []"
                  value-key="agreementId"
                  label-key="name"
                  searchable
                  :handle-search="false"
                  on-select-return-as-object
                  class="px-2 min-w-64"
                  @select="emit('link-to-customer', modelValue, $event)"
                >
                  <template #option="{ option }">
                    <div class="flex flex-col w-full text-wrap">
                      <div>{{ option.name }}</div>
                      <div class="text-sm text-tertiary">
                        [{{ option.vrm }}] {{ option.vehicleMake }} {{ option.vehicleModel }}
                      </div>
                    </div>
                  </template>
                </Select>
              </Dropdown>
            </div>
            <div class="text-sm text-quaternary">
              {{ modelValue.commFrom }}
            </div>
          </div>
          <div class="flex items-center gap-2 ml-2">
            <Icon
              type="arrow-turn-down-right"
              class="text-gray-400"
            />
            <div
              v-tippy="{ content: modelValue.commTo }"
              class="text-secondary text-sm"
            >
              {{ modelValue.recipient?.name }}
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="w-full text-md text-secondary">
          <div
            v-if="modelValue.type === 'call'"
            class="flex items-center gap-4"
          >
            <Button
              v-if="!modelValue.isActioned"
              label="Call back"
              icon="phone"
              severity="secondary"
              outlined
              size="md"
              @click="emit('call-back', modelValue)"
            />
            <AudioPlayer
              v-if="modelValue.recordingUrl"
              :audio-src="modelValue.recordingUrl"
              class="w-[300px]"
            />
            <div
              v-else
              class="flex gap-2 items-center text-quaternary"
            >
              <Icon type="voicemail" />
              <div>No voicemail left</div>
            </div>
          </div>
          <template v-else>
            {{
              modelValue.emailBody || modelValue.smsMessage
                ? ellipsize(modelValue.emailBody || modelValue.smsMessage, 200)
                : ''
            }}
          </template>
        </div>

        <!-- Action Buttons -->
        <div class="flex-shrink-0 min-w-[120px] flex justify-end ml-6">
          <!-- If actioned, show who completed it and when -->
          <div
            v-if="modelValue.isActioned"
            class="flex gap-2 items-center"
          >
            <Chip
              v-tippy="'Completed by'"
              :avatar="modelValue.acceptedBy?.profilePicUrl"
              :avatar-text="modelValue.acceptedBy?.initials"
              :label="modelValue.acceptedBy?.name"
              size="md"
            />
            <Chip
              v-tippy="'Completed at'"
              icon="clock"
              :label="$dayjs(modelValue.acceptedAt).format('HH:mm')"
              size="md"
            />
          </div>
          <!-- If not actioned, show action buttons -->
          <template v-else>
            <Button
              v-if="!modelValue.acceptedBy"
              size="md"
              label="Accept"
              block
              @click="emit('accept-conversation', modelValue)"
            />
            <div
              v-else
              class="w-full flex justify-end"
            >
              <div
                v-if="modelValue.acceptedBy.id === authStore.details.id"
                class="w-full flex gap-4 items-center justify-end"
              >
                <div class="flex gap-1 items-center">
                  <Chip
                    v-if="modelValue.acceptedAt"
                    v-tippy="'Time accepted'"
                    size="md"
                    :avatar="modelValue.acceptedBy?.profilePicUrl"
                    :avatar-text="modelValue.acceptedBy?.initials"
                    :label="$dayjs(modelValue.acceptedAt).format('HH:mm')"
                  />
                </div>
                <Button
                  v-tippy="'Release'"
                  severity="danger"
                  outlined
                  icon="xmark"
                  @click="emit('release-conversation', modelValue)"
                />
                <Button
                  v-tippy="'Complete'"
                  size="md"
                  icon="check"
                  severity="success"
                  @click="emit('complete-conversation', modelValue)"
                />
              </div>
              <div
                v-else
                class="flex gap-2 items-center"
              >
                <Chip
                  v-tippy="'Accepted by'"
                  :avatar="modelValue.acceptedBy?.profilePicUrl"
                  :avatar-text="modelValue.acceptedBy?.initials"
                  :label="modelValue.acceptedBy?.name"
                  size="md"
                />
                <Chip
                  v-tippy="'Time accepted'"
                  icon="clock"
                  :label="$dayjs(modelValue.acceptedAt).format('HH:mm')"
                  size="md"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// vue
import { computed, ref } from 'vue'
// components
import Button from '@/components/button/Button.vue'
import Icon from '@/components/icon/Icon.vue'
import AudioPlayer from '@/components/audio-player/AudioPlayer.vue'
import Chip from '@/components/chip/Chip.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import Select from '@/components/forms/Select.vue'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'

// utilities
import { formatTimestampToParts } from '@/utilities/date-time/format'
import { ellipsize } from '@/utilities/string/ellipsize'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  searchTerm: {
    type: String,
    default: ''
  },
  agreementsList: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'update:modelValue',
  'update:searchTerm',
  'link-to-customer',
  'accept-conversation',
  'release-conversation',
  'complete-conversation',
  'call-back'
])

const authStore = useAuthStore()

/**
 * Selected agreement id or object from search results.
 */
const selectedAgreementId = ref(null)

/**
 * Proxy for search term to emit updates.
 */
const searchTermModel = computed({
  get: () => props.searchTerm,
  set: value => {
    emit('update:searchTerm', value)
  }
})

/**
 * Route link to the agreement view for this conversation.
 * @returns {import('vue-router').RouteLocationRaw} Agreement route location.
 */
const agreementLink = computed(() => {
  try {
    return { path: `/agreement/${props.modelValue?.agreementId ?? ''}` }
  } catch (error) {
    console.error('Failed to build agreement link', error)
    return { path: '/agreement/' }
  }
})

/**
 * Get icon type based on conversation type
 * @param {string} type - Conversation type (sms, email, call)
 * @returns {string} Icon type
 */
const getTypeIcon = type => {
  switch (type) {
    case 'sms':
      return 'sms'
    case 'email':
      return 'envelope'
    case 'call':
      return 'phone'
    default:
      return 'question'
  }
}
</script>

<style scoped>
.missed-conversation.delayed-warning .apply-severity {
  background-color: var(--bg-warning-light);
  border-color: var(--border-warning-light);
  color: var(--text-warning);
}
.missed-conversation.delayed-danger .apply-severity {
  background-color: var(--bg-error-light);
  border-color: var(--border-error-light);
  color: var(--text-error);
}
</style>
